import { useState, useEffect, useCallback } from 'react';
import { Send, Loader2, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface WhatsAppSender {
  id: string;
  phone_number: string;
  friendly_name?: string;
  display_name?: string;
}

interface WhatsAppTemplate {
  id: string;
  friendly_name: string;
  body_text: string;
  approval_status: string;
  variables?: Record<string, string> | string[];
}

interface SendersResponse {
  senders: WhatsAppSender[];
}

interface TemplatesResponse {
  templates: WhatsAppTemplate[];
}

interface ResolvedVariablesResponse {
  variables: Record<string, string>;
}

interface WhatsAppTemplateSendModalProps {
  open: boolean;
  onClose: () => void;
  conversationId?: string | null;
  contactPhone: string;
  contactName: string;
  leadId?: string;
  onSuccess?: () => void;
}

export default function WhatsAppTemplateSendModal({
  open,
  onClose,
  conversationId,
  contactPhone,
  contactName,
  leadId,
  onSuccess,
}: WhatsAppTemplateSendModalProps) {
  const { toast } = useToast();
  const [senders, setSenders] = useState<WhatsAppSender[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedSender, setSelectedSender] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [sendersRes, templatesRes] = await Promise.all([
        apiClient.get<SendersResponse>('/messaging/senders?channel=whatsapp'),
        apiClient.get<TemplatesResponse>('/messaging/templates?status=approved&limit=100'),
      ]);
      const s = sendersRes.data?.senders || [];
      const t = templatesRes.data?.templates || [];
      setSenders(s);
      setTemplates(t);
      if (s.length > 0) {
        setSelectedSender(s[0]!.phone_number! || s[0]!.id!);
      }
    } catch {
      toast({ title: 'Errore caricamento dati', variant: 'destructive' });
    } finally {
      setLoadingData(false);
    }
  }, [toast]);

  useEffect(() => {
    if (open) {
      setSelectedSender('');
      setSelectedTemplateId('');
      setTemplateVariables({});
      setSending(false);
      loadData();
    }
  }, [open, loadData]);

  useEffect(() => {
    if (selectedTemplateId && open) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        const numericVars = template.body_text?.match(/\{\{(\d+)\}\}/g) || [];
        const vars: Record<string, string> = {};
        numericVars.forEach(v => {
          const num = v.replace(/[{}]/g, '');
          vars[num] = '';
        });
        setTemplateVariables(vars);

        const variableMapping = template.variables && !Array.isArray(template.variables) ? template.variables : null;
        if (variableMapping && Object.keys(variableMapping).length > 0 && contactPhone) {
          apiClient.get<ResolvedVariablesResponse>(`/messaging/resolve-variables?phone=${encodeURIComponent(contactPhone)}`)
            .then(res => {
              const resolvedVars = res.data?.variables;
              if (resolvedVars) {
                const resolved: Record<string, string> = {};
                Object.entries(variableMapping).forEach(([num, varKey]) => {
                  if (resolvedVars[varKey]) {
                    resolved[num] = resolvedVars[varKey];
                  }
                });
                setTemplateVariables(prev => ({ ...prev, ...resolved }));
              }
            })
            .catch(err => console.error('Failed to resolve template variables:', err));
        }
      }
    } else if (!selectedTemplateId) {
      setTemplateVariables({});
    }
  }, [selectedTemplateId, templates, contactPhone, open]);

  const handleSend = async () => {
    if (!selectedSender || !selectedTemplateId) {
      toast({ title: 'Seleziona mittente e template', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      if (conversationId) {
        await apiClient.post<{ message_sid: string }>(`/messaging/conversations/${conversationId}/send-template`, {
          template_id: selectedTemplateId,
          template_variables: templateVariables,
          from_number: selectedSender,
        });
      } else {
        await apiClient.post<{ conversation_id: string }>('/messaging/conversations', {
          contact_phone: contactPhone,
          contact_name: contactName,
          channel: 'whatsapp',
          from_number: selectedSender,
          template_id: selectedTemplateId,
          template_variables: templateVariables,
          lead_id: leadId,
        });
      }
      toast({ title: 'Template inviato', description: `Messaggio inviato a ${contactName}` });
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Errore sconosciuto';
      toast({ title: 'Errore invio template', description: message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-600" />
            Invia Template WhatsApp
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Destinatario</Label>
            <div className="mt-1 p-2 bg-gray-50 rounded-md text-sm text-gray-700">
              {contactName} — {contactPhone}
            </div>
          </div>

          <div>
            <Label>Numero mittente *</Label>
            {loadingData ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
              </div>
            ) : (
              <Select value={selectedSender} onValueChange={setSelectedSender}>
                <SelectTrigger className={!selectedSender ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Seleziona numero mittente" />
                </SelectTrigger>
                <SelectContent>
                  {senders.map((s) => (
                    <SelectItem key={s.id} value={s.phone_number || s.id}>
                      {s.friendly_name || s.display_name || s.phone_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <Label>Template WhatsApp *</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger className={!selectedTemplateId ? 'border-red-300' : ''}>
                <SelectValue placeholder="Seleziona template approvato" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.friendly_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTemplate && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-gray-700">
              <p className="font-medium text-green-700 mb-1">Anteprima:</p>
              <p className="whitespace-pre-wrap">{selectedTemplate.body_text}</p>
            </div>
          )}

          {Object.keys(templateVariables).length > 0 && (() => {
            const varMapping = selectedTemplate?.variables && !Array.isArray(selectedTemplate.variables) ? selectedTemplate.variables : null;
            return (
              <div className="space-y-2">
                <Label className="text-sm">Variabili</Label>
                {Object.keys(templateVariables).map(key => {
                  const mappedVar = varMapping?.[key];
                  const isAutoFilled = !!templateVariables[key] && !!mappedVar;
                  return (
                    <div key={key}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Label className="text-xs text-gray-600">{`{{${key}}}`}</Label>
                        {mappedVar && (
                          <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${isAutoFilled ? 'bg-green-100 text-green-700' : ''}`}>
                            {mappedVar.replace(/_/g, ' ')}
                            {isAutoFilled && ' ✓'}
                          </Badge>
                        )}
                      </div>
                      <Input
                        value={templateVariables[key]}
                        onChange={(e) => setTemplateVariables(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={mappedVar ? `Auto: ${mappedVar.replace(/_/g, ' ')}` : `Valore per ${key}`}
                        className="bg-white"
                      />
                    </div>
                  );
                })}
              </div>
            );
          })()}

          <Button
            onClick={handleSend}
            disabled={!selectedSender || !selectedTemplateId || sending}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {sending ? 'Invio in corso...' : 'Invia Template'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
